import classnames from 'classnames';
import IconTrash from '@scramble/design-system/icons/trash-01.svg'
import IconRetry from '@scramble/design-system/icons/refresh-ccw-01.svg'
import { UploadFile, FileUploaderLabels } from './types';
import { getFileIcon } from './icons';
import s from './FileItem.module.scss';

type FileItemProps = {
  file: UploadFile;
  onRemove: (key: string) => void;
  onRetry: (key: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  labels?: FileUploaderLabels;
};

type VisualState = 'indeterminate' | 'progress' | 'uploaded' | 'failed';

function getVisualState(file: UploadFile): VisualState {
  if (file.status === 'error') return 'failed';
  if (file.status === 'uploaded' || !file.status) return 'uploaded';
  if (file.status === 'idle') return 'indeterminate';
  if (file.status === 'uploading' && file.progress === 0) return 'indeterminate';
  return 'progress';
}

function formatBytes(bytes: number): string {
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1_024) return `${(bytes / 1_024).toFixed(0)} KB`;
  return `${bytes} B`;
}

export const FileItem = ({
  file,
  onRemove,
  onRetry,
  disabled = false,
  readOnly = false,
  labels,
}: FileItemProps) => {
  const visualState = getVisualState(file);
  const isFailed = visualState === 'failed';
  const isUploaded = visualState === 'uploaded';
  const isIndeterminate = visualState === 'indeterminate';
  const isProgress = visualState === 'progress';
  const showProgressTrack = isIndeterminate || isProgress || isUploaded || isFailed;

  const IconFile = getFileIcon(file.name);

  const metaStatusText = (() => {
    if (isFailed) return null;
    if (isUploaded) return labels?.statusUploaded ?? 'Uploaded';
    if (isIndeterminate) return labels?.statusUploading ?? 'Uploading...';
    return `${file.progress}%`;
  })();

  return (
    <div
      className={classnames(s.file_item, isFailed && s['file_item--failed'])}
      data-state={visualState}
    >
      <div className={s.file_item__container}>
        <div className={s.file_item__icon} aria-hidden="true">
          <img src={IconFile} alt={file.name} width={40} height={40} />
        </div>

        <div className={s.file_item__text_section}>
          <span className={s.file_item__name} title={file.name}>
            {file.name}
          </span>

          <div className={s.file_item__meta}>
            {file.size != null && <span>{formatBytes(file.size)}</span>}
            {file.size != null && <span aria-hidden="true">•</span>}

            {isFailed ? (
              <span className={s.file_item__meta_failed}>{labels?.statusFailed ?? 'Failed'}</span>
            ) : (
              metaStatusText != null && <span>{metaStatusText}</span>
            )}
          </div>
        </div>

        <div className={s.file_item__actions}>
          {isFailed && (
            <button
              type="button"
              className={s.file_item__action_button}
              onClick={() => onRetry(file._key)}
              disabled={disabled}
              aria-label={labels?.retryAriaLabel ? labels.retryAriaLabel(file.name) : `Retry upload for ${file.name}`}
            >
              <img src={IconRetry} width={20} height={20} alt="" />
            </button>
          )}

          {!readOnly && (
            <button
              type="button"
              className={s.file_item__action_button}
              onClick={() => onRemove(file._key)}
              disabled={disabled}
              aria-label={labels?.removeAriaLabel ? labels.removeAriaLabel(file.name) : `Remove ${file.name}`}
            >
              <img src={IconTrash} width={20} height={20} alt="" />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar — thin line at the bottom of the card */}
      {showProgressTrack && (
        <div className={s.file_item__progress_track} aria-hidden="true">
          <div
            className={classnames(
              s.file_item__progress_bar,
              isIndeterminate && s['file_item__progress_bar--indeterminate'],
              isProgress && s['file_item__progress_bar--uploading'],
              isUploaded && s['file_item__progress_bar--uploaded'],
              isFailed && s['file_item__progress_bar--error']
            )}
            style={isProgress ? { width: `${file.progress}%` } : undefined}
            role="progressbar"
            aria-valuenow={isProgress ? file.progress : undefined}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      )}
    </div>
  );
};
